import { useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FINAL_QUESTIONS, FINAL_TEXT_QUESTIONS, type Survey } from '@cf/shared'
import { toast } from 'sonner'
import { AppShell, PageBody, initialsOf } from '@/components/AppShell'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Choice } from '@/components/ui/choice'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { reviewApi } from '@/lib/api'
import { plural } from '@/lib/utils'
import { flatten, isAnswered, useAnswers, useInvite, useReport } from './useReview'

/** The last screen: the questions about the set as a whole, then send. */
export function SummaryPage() {
  const { token } = useParams({ from: '/r/$token' })
  const invite = useInvite(token)
  const report = useReport(token)
  const answers = useAnswers(token)
  const client = useQueryClient()
  const [sent, setSent] = useState(false)

  const submit = useMutation({
    mutationFn: (survey: Survey) => reviewApi.submit(token, survey),
    onSuccess: () => {
      setSent(true)
      void client.invalidateQueries({ queryKey: ['invite', token] })
      void client.invalidateQueries({ queryKey: ['answers', token] })
    },
    onError: (error) =>
      toast.error('Nie udało się wysłać', {
        description: error instanceof Error ? error.message : undefined,
      }),
  })

  if (!invite.data || answers.isLoading) {
    return (
      <AppShell>
        <PageBody className="flex max-w-[760px] flex-col gap-4">
          <Skeleton className="h-64 w-full rounded-xl" />
        </PageBody>
      </AppShell>
    )
  }

  const boards = flatten(report.data?.axes ?? [])
  const answered = Object.values(answers.data?.boards ?? {}).filter(isAnswered).length
  const missing = boards.length - answered
  const alreadySent = sent || Boolean(invite.data.submittedAt)

  return (
    <AppShell
      subtitle={invite.data.dataset.label}
      identity={{
        initials: initialsOf(invite.data.nick),
        name: invite.data.displayName ?? invite.data.nick,
        detail: invite.data.title,
      }}
    >
      <PageBody className="flex max-w-[760px] flex-col gap-5">
        {alreadySent ? (
          <Card>
            <CardHeader>
              <CardTitle className="font-serif text-xl">Wysłane. Dziękujemy.</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-start gap-4 text-sm leading-relaxed text-ink-soft">
              <p>
                Twoje odpowiedzi trafiły do nas: {answered} z {boards.length}{' '}
                {plural(boards.length, 'szachownicy', 'szachownic', 'szachownic')} z oceną
                opisu. Runda jest zamknięta dla edycji. Jeśli chcesz coś jeszcze poprawić,
                napisz, a otworzymy ją z powrotem.
              </p>
              <Button variant="outline" asChild>
                <Link to="/r/$token/report" params={{ token }}>
                  Wróć do raportu
                </Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <SurveyForm
            token={token}
            initial={answers.data?.survey ?? {}}
            missing={missing}
            pending={submit.isPending}
            onSubmit={(survey) => submit.mutate(survey)}
          />
        )}
      </PageBody>
    </AppShell>
  )
}

/**
 * Seeded once from whatever was saved before, then owned by the form.
 *
 * Rendered only after the answers have loaded, so the initial value is the
 * real one and nothing has to be copied into state by an effect afterwards.
 */
function SurveyForm({
  token,
  initial,
  missing,
  pending,
  onSubmit,
}: {
  token: string
  initial: Survey
  missing: number
  pending: boolean
  onSubmit: (survey: Survey) => void
}) {
  const [draft, setDraft] = useState<Survey>(initial)

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-xl">Zostały pytania o całość</CardTitle>
        </CardHeader>
        <CardContent>
          <FieldGroup>
            {FINAL_QUESTIONS.map((question) => (
              <Field key={question.id}>
                <FieldLabel>{question.prompt}</FieldLabel>
                <Choice
                  ariaLabel={question.prompt}
                  options={question.options}
                  value={draft.exercises ?? null}
                  onChange={(value) =>
                    setDraft((current) => ({
                      ...current,
                      exercises: (value ?? undefined) as Survey['exercises'],
                    }))
                  }
                />
              </Field>
            ))}
            {FINAL_TEXT_QUESTIONS.map((question) => (
              <Field key={question.id}>
                <FieldLabel htmlFor={question.id}>{question.prompt}</FieldLabel>
                <Textarea
                  id={question.id}
                  rows={4}
                  value={draft[question.id] ?? ''}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      [question.id]: event.target.value,
                    }))
                  }
                />
              </Field>
            ))}
          </FieldGroup>
        </CardContent>
      </Card>

      {missing > 0 ? (
        <Alert>
          <AlertTitle>
            {missing}{' '}
            {plural(
              missing,
              'szachownica bez oceny',
              'szachownice bez oceny',
              'szachownic bez oceny',
            )}
          </AlertTitle>
          <AlertDescription>
            Możesz wysłać tak jak jest, ale{' '}
            <Link to="/r/$token/verify" params={{ token }} className="underline">
              warto je uzupełnić
            </Link>
            .
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <Button variant="outline" asChild>
          <Link to="/r/$token/verify" params={{ token }}>
            Wróć do szachownic
          </Link>
        </Button>
        <Button disabled={pending} onClick={() => onSubmit(draft)}>
          {pending ? 'Wysyłanie…' : 'Wyślij feedback'}
        </Button>
      </div>
    </>
  )
}
